import { ButtonGradientPrimary } from '@amelio/ui-library';
import { Trans, useTranslation } from 'react-i18next';

import { PlusIcon } from '../PrepareMeetingIcons';

import { PrepareMeetingArrowIllustration } from '@/components/Illustrations/Illustration';

interface PrepareMeetingReadySectionProps {
    onMeet: () => void;
    isDisabled: boolean;
    employeeFirstName?: string;
}

export const PrepareMeetingReadySection = ({
    onMeet,
    isDisabled,
    employeeFirstName,
}: PrepareMeetingReadySectionProps) => {
    const { t } = useTranslation();

    /* Three mutually exclusive width variants below, never a base utility plus a `md:` one: this
       app emits the plain utility after its own responsive variant, so a base class would win past
       the breakpoint (see src/responsive-utilities.css).

       Above the section, the margin is the whole gap — the stack's own 24px collapses into it.
       Below it, the page's own bottom padding adds to the margin, so each value is the air this
       section should end on, less the 32px the page already contributes. */
    return (
        <div
            className={
                'relative flex flex-col items-start gap-8 max-md:mt-22 max-md:mb-16 md:flex-row md:gap-12 md:max-lg:mt-35 md:max-lg:mb-29 lg:mt-60 lg:mb-79'
            }
        >
            <div className={'flex w-full flex-col items-start justify-center pt-4 md:w-1/2'}>
                <h2
                    className={
                        'max-sm:text-heading-sm w-full text-left leading-tight font-semibold text-gray-800 sm:text-[30px]'
                    }
                >
                    {t('postReviewPrepareMeeting.readyToMeet.title')}
                    {employeeFirstName && (
                        <>
                            <br />
                            {employeeFirstName}
                        </>
                    )}
                </h2>
            </div>

            <PrepareMeetingArrowIllustration
                className={
                    'pointer-events-none absolute top-[5.5rem] left-[19%] z-10 w-[28%] max-md:hidden md:top-[5.75rem] md:left-[21%] md:w-[35%] lg:top-[5rem] lg:left-[17%] lg:w-[34%] xl:top-[4.8rem] xl:left-[21%] xl:w-[32%] 2xl:left-[22%] 2xl:w-[30%]'
                }
            />

            <div
                className={
                    'ml-auto flex w-full flex-col gap-4 rounded-xl border border-purple-200/30 bg-white/50 p-7 backdrop-blur-sm transition-all duration-300 hover:border-purple-300/60 md:w-1/2 md:p-6'
                }
            >
                <p className={'text-md leading-relaxed text-gray-500'}>
                    {t('postReviewPrepareMeeting.readyToMeet.subtitle')}
                </p>
                <ButtonGradientPrimary
                    onClick={onMeet}
                    className={
                        'flex w-auto items-center gap-3 self-start rounded-lg px-8 py-4 text-lg font-medium text-white shadow-md transition-all duration-200 hover:opacity-95 hover:shadow-lg disabled:cursor-not-allowed disabled:opacity-50'
                    }
                    type={'button'}
                    disabled={isDisabled}
                    leftIcon={
                        <PlusIcon
                            className={'h-8 w-8'}
                            strokeWidth={1.5}
                        />
                    }
                >
                    <Trans
                        i18nKey={'postReviewPrepareMeeting.readyToMeet.button'}
                        values={{ firstName: employeeFirstName }}
                    />
                </ButtonGradientPrimary>
            </div>
        </div>
    );
};
